import { FC, useMemo } from 'react';
import { TbCalendarStats } from 'react-icons/tb';

import { MetricChart } from '@/shared/components/data-visualization/recharts';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import GlideDataGridTable from '@/shared/components/tables/GlideDataGridTable';
import { dayjs, FundamentalValuationsType } from '@/utilities';

import { useFinancialOverviewViewPreference } from '../../hooks/useFinancialOverviewViewPreference';
import type { FinancialOverviewCorporateDebtData as CorporateDebtData } from '../../types';

interface DebtTrendViewProps {
    preferenceKey?: string;
    section: CorporateDebtData;
    tickerSymbol?: string;
}

type DebtPeriod = 'annual' | 'quarter';

const isDebtPeriod = (value: unknown): value is DebtPeriod => value === 'annual' || value === 'quarter';

const DEBT_LINE_ITEMS = [
    'totalDebt',
    'netDebt',
    'longTermDebt',
    'shortTermDebt',
    'unclassifiedDebt',
    'cashAndEquivalents',
];

const DebtTrendView: FC<DebtTrendViewProps> = ({ preferenceKey = 'default', section, tickerSymbol }) => {
    const hasQuarterlyHistory = Boolean(section.quarterlyHistory?.length);
    const [savedPeriod, setPeriod] = useFinancialOverviewViewPreference(
        preferenceKey,
        'debtPeriod',
        'annual',
        isDebtPeriod,
    );
    const period = savedPeriod === 'quarter' && !hasQuarterlyHistory ? 'annual' : savedPeriod;
    const rows = useMemo(() => {
        const history = period === 'quarter' ? (section.quarterlyHistory ?? []) : section.history;

        return history
            .map((point) => {
                const date = point.date && dayjs(point.date).isValid() ? dayjs(point.date) : undefined;
                const periodLabel =
                    period === 'annual'
                        ? String(date?.year() ?? point.date ?? 'Reported')
                        : date
                          ? `Q${date.quarter()} ${date.year()}`
                          : (point.date ?? 'Reported');

                return {
                    ...point,
                    date: point.date ?? periodLabel,
                    periodLabel,
                };
            })
            .sort((left, right) => dayjs(left.date).valueOf() - dayjs(right.date).valueOf());
    }, [period, section.history, section.quarterlyHistory]);
    const company = tickerSymbol
        ? { companyName: tickerSymbol, symbol: tickerSymbol }
        : { companyName: 'Company', symbol: 'primary' };

    return (
        <div
            aria-label={`${period === 'annual' ? 'Annual' : 'Quarterly'} debt trend`}
            style={{ display: 'grid', gap: '1rem' }}
        >
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: '11rem' }}>
                    <DropdownButtonSelect<DebtPeriod>
                        aria-label='Select debt reporting cadence'
                        header={
                            <DropdownMenuLabel icon={<TbCalendarStats size='0.875rem' />}>
                                Reporting Cadence
                            </DropdownMenuLabel>
                        }
                        matchTriggerWidth={false}
                        menuMinWidth='14rem'
                        onChange={(value) => value && setPeriod(value)}
                        options={[
                            {
                                description: 'Compare fiscal-year debt balances.',
                                label: 'Annual',
                                value: 'annual',
                            },
                            {
                                description: hasQuarterlyHistory
                                    ? 'Compare reported fiscal-quarter debt balances.'
                                    : 'Quarterly debt history is not available.',
                                disabled: !hasQuarterlyHistory,
                                label: 'Quarterly',
                                value: 'quarter',
                            },
                        ]}
                        placement='bottomRight'
                        size='small'
                        value={period}
                    />
                </div>
            </div>
            <div style={{ height: '18rem', minHeight: 0 }}>
                <MetricChart
                    chartedLineItem='totalDebt'
                    company={company}
                    data={rows}
                    lineItemLabels={{ netDebt: 'Net Debt', totalDebt: 'Total Debt' }}
                    segmentedValue={period}
                    setSegmentedValue={(value) => setPeriod(value === 'quarter' ? 'quarter' : 'annual')}
                    tags={['totalDebt', 'netDebt']}
                    title='Corporate Debt Trend'
                    type={FundamentalValuationsType.VALUE}
                />
            </div>
            <div style={{ height: '13rem', minHeight: 0 }}>
                <GlideDataGridTable
                    company={company}
                    data={rows}
                    height='100%'
                    schema={DEBT_LINE_ITEMS}
                    segmentedValue={period}
                    setSegmentedValue={(value) => setPeriod(value === 'quarter' ? 'quarter' : 'annual')}
                    type={FundamentalValuationsType.VALUE}
                />
            </div>
        </div>
    );
};

export default DebtTrendView;
